import { useEffect, useMemo, useState } from "react";
import Reports from "./Reports";
import WorkInProgress from "./WorkInProgress";
import Footer from "../../../core/common/Footer";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import { PageLoader } from "../../../core/common/loader/PageLoader";
import { SyncOutlined, FileTextOutlined } from "@ant-design/icons";
// Strict type
type TabLabel =
  // | "Application Submitted"
  | "Work In Progress"
  // | "Interim Reports"
  | "All Reports";

type TabItem = {
  status_id: string;
  label: TabLabel;
  count: number;
};

// FIX: Store COMPONENT, not JSX
const componentMap: Record<TabLabel, React.ComponentType<any>> = {
  // "Application Submitted": ApplicationSubmitted,
  "Work In Progress": WorkInProgress,
  // "Interim Reports": InterimReports,
  "All Reports": Reports,
};

const iconMap: Record<TabLabel, React.ReactNode> = {
  "Work In Progress": <SyncOutlined />,
  "All Reports": <FileTextOutlined />,
};

const ACTIVE_TAB_KEY = "verifier-active-track-case-tab";

const TrackCaseProgram = () => {
  // Restore from localStorage directly in initial state
  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      return localStorage.getItem(ACTIVE_TAB_KEY) || "";
    } catch {
      return "";
    }
  });

  // API
  const { data: tabCount, loading } = useApi(webservice_url.fetch_tab_count, {
    method: "GET",
    autoCall: true,
  });

  // Build tabs with Component reference
  const dynamicTabs = useMemo(
    () =>
      ((tabCount?.data as TabItem[] | undefined) ?? []).map((item) => ({
        id: item?.label,
        status_id: item?.status_id ?? "",
        label: item?.label,
        count: item?.count,
        Component: componentMap[item?.label],
      })),
    [tabCount],
  );

  // Default tab: agar activeTab empty ya invalid hai to pehla tab
  useEffect(() => {
    if (dynamicTabs.length === 0) return;
    const exists = dynamicTabs.some((tab) => tab?.label === activeTab);

    if (!exists) {
      setActiveTab(dynamicTabs[0]?.label);
    }
  }, [dynamicTabs, activeTab]);

  const activeTabData = dynamicTabs.find((tab) => tab?.label === activeTab);
  const ActiveComponent = activeTabData?.Component;

  // Ye function missing tha, isi wajah se click kaam nahi kar raha tha
  const handleTabClick = (tab: { label: string }) => {
    setActiveTab(tab.label);
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, tab.label);
    } catch (err) {
      console.error("Failed to save active tab to localStorage:", err);
    }
  };

  const CaseMark = () => (
    <svg
      className="tcp-case-mark"
      viewBox="0 0 40 32"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="14" width="4" height="18" rx="1.5" />
      <rect x="9" y="8" width="4" height="24" rx="1.5" />
      <rect x="18" y="0" width="4" height="32" rx="1.5" />
      <rect x="27" y="10" width="4" height="22" rx="1.5" />
      <rect x="36" y="18" width="4" height="14" rx="1.5" />
    </svg>
  );

  return (
    <div className="page-wrapper">
      <div className="content p-2 p-md-3">
        {loading ? (
          <PageLoader />
        ) : (
          <>
            <div className="tcp-header-title-row d-flex gap-3 mb-3">
              <CaseMark />
              <div>
                <h4 className="tcp-title">Live Case Progress</h4>
                <p className="tcp-subtitle">
                  Monitor ongoing background verification cases, track screening
                  progress, and review cases assigned for verification.
                </p>
              </div>
            </div>
            <ul className="invite-tabs">
              {dynamicTabs.map((tab) => (
                <li key={tab.id}>
                  <button
                    type="button"
                    className={`invite-btn ${
                      activeTab === tab.label ? "active" : ""
                    }`}
                    onClick={() => handleTabClick(tab)}
                  >
                    <span className="d-flex align-items-center justify-content-center gap-2">
                      {iconMap[tab.label as TabLabel]}
                      <span>{tab.label}</span>
                    </span>

                    {typeof tab.count === "number" && (
                      <span className="d-block fw-semibold">{tab.count}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>

            {/* Tab Content */}
            <div className="mt-4 pt-2">
              {ActiveComponent && (
                <ActiveComponent status_id={activeTabData?.status_id} />
              )}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default TrackCaseProgram;
